import * as signalR from '@microsoft/signalr'

export type SignalRHubHandler = (payload: unknown) => void

export type SignalRHubOptions = {
  hubUrl: string
  accessTokenFactory: () => Promise<string>
  handlers: Record<string, SignalRHubHandler>
}

/**
 * Connexion générique à un hub SignalR (réutilisable pour n'importe quel hub) : token via
 * `accessTokenFactory`, reconnexion auto, handlers `on(...)` enregistrés avant `start()`.
 */
export class SignalRHubUtils {
  private connection: signalR.HubConnection | null = null

  /**
   * Ouvre la connexion. No-op hors client ou si une connexion est déjà ouverte / en cours.
   * @param {SignalRHubOptions} options - URL du hub, fournisseur de token, handlers par événement.
   * @returns {Promise<void>}
   */
  async start(options: SignalRHubOptions): Promise<void> {
    if (!import.meta.client) return
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) return

    if (!this.connection) this.connection = this.build(options)
    try {
      await this.connection.start()
    } catch (error: unknown) {
      console.error('[SignalRHubUtils] échec du démarrage:', error)
    }
  }

  /**
   * Ferme la connexion et repart d'un état propre.
   * @returns {Promise<void>}
   */
  async stop(): Promise<void> {
    if (!this.connection) return
    try {
      await this.connection.stop()
    } finally {
      this.connection = null
    }
  }

  /**
   * Construit la connexion : handlers enregistrés avant `start()` (sinon un message reçu entre-temps est perdu).
   * @param {SignalRHubOptions} options - URL du hub, fournisseur de token, handlers par événement.
   * @returns {signalR.HubConnection} Connexion prête à démarrer.
   */
  private build(options: SignalRHubOptions): signalR.HubConnection {
    const hub: signalR.HubConnection = new signalR.HubConnectionBuilder()
      .withUrl(options.hubUrl, { accessTokenFactory: options.accessTokenFactory })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    for (const [eventName, handler] of Object.entries(options.handlers)) {
      hub.on(eventName, handler)
    }

    hub.onclose((error?: Error): void => {
      if (error) console.error('[SignalRHubUtils] connexion fermée:', error)
    })

    return hub
  }
}
